import styled from 'styled-components'
import { HeightLineClamp } from '@/features/shared/components/LineClamp'
import { NutritionalWarnings } from '@/features/shop/components/ProductDetailCommon/NutritionalWarnings'
import { type Product } from '@/features/shop/services/Product/types'
import { Body } from '@shipt/design-system-typography'
import { sanitizeHTML } from '@/features/shared/utils/sanitize'
import { trackElementClicked } from '@/features/shared/analytics/element'
import dynamic from 'next/dynamic'
import { ClientOnly } from '@/features/shared/components/ClientOnly'
import { type TermsByState } from '@/features/cms/components/CMS/types'
import { Accordion, AccordionGroup, AccordionItem } from '@shipt/design-system-accordion'
import { renderElement } from '@/features/cms/components/CMS/RichTextRenderer'
import { WarningTriangleIcon } from '@shipt/design-system-icons'
import { Box, Column } from '@shipt/design-system-layouts'
import { capitalizeFirstLetter } from '@/features/shared/utils/formatter'

const NutritionFacts = dynamic(
  async () => (await import('@shipt/shipt-nutrition-label')).NutritionLabel,
  { ssr: false },
)
const TAB_DETAILS_TITLE = 'Product details'
const TAB_SPECIFICATIONS_TITLE = 'Specifications'
const TAB_HIGHLIGHTS_TITLE = 'Highlights'

type Props = {
  product: Product
  alcoholTerms?: TermsByState
  suppressNutritionalWarnings?: boolean
}

export const ProductTabPanel = ({
  product,
  alcoholTerms,
  suppressNutritionalWarnings = false,
}: Props) => {
  const {
    name,
    price,
    description,
    ingredients,
    allergenInfo,
    chokingHazard,
    nutritionalDetails,
    nutritionalWarnings,
    specifications,
    highlights,
  } = product

  const hasProductDescription = Boolean(description) || !!alcoholTerms?.data.show_on_pdp

  const nutritionDetailsProducts = nutritionalDetails.products ?? []
  const hasProductNutrition = Boolean(ingredients || nutritionDetailsProducts.length > 0)
  const NUTRITION_OR_INGREDIENTS_TITLE = nutritionDetailsProducts.length
    ? 'Nutrition'
    : 'Ingredients'

  const showNutritionalWarnings = Boolean(
    !suppressNutritionalWarnings &&
    !chokingHazard &&
    !allergenInfo &&
    nutritionalWarnings.length > 0,
  )

  // The details tab is only expanded on first load when there are no
  // highlights. We clamp its content (max-height + "Show more") only in that
  // case so exactly one open-by-default section shows the clamp; a collapsed
  // section renders its full text (also keeping it visible to crawlers).
  const detailsDefaultExpanded = !highlights.length

  const detailsBody = (
    <>
      <Body dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }} />
      {!!showNutritionalWarnings && <NutritionalWarnings warnings={nutritionalWarnings} />}
    </>
  )

  return (
    <TabPanelContainer>
      <AccordionGroup key={product.id} hasLeadingBorder={false}>
        {highlights.length > 0 && (
          <AccordionItem
            id={TAB_HIGHLIGHTS_TITLE}
            title={TAB_HIGHLIGHTS_TITLE}
            headerAs="h2"
            shouldPersistContent
            defaultExpanded
            onClick={() => {
              trackElementClicked({
                content: 'product_highlights',
                type: 'tab',
              })
            }}
          >
            <HeightLineClamp height={150} ariaLabel={TAB_HIGHLIGHTS_TITLE}>
              <Column as="ul" spacing="sm" margin={[0]}>
                {highlights.map((highlight) => (
                  <li key={highlight}>{capitalizeFirstLetter(highlight)}</li>
                ))}
              </Column>
            </HeightLineClamp>
          </AccordionItem>
        )}
        {!!hasProductDescription && (
          <AccordionItem
            id={TAB_DETAILS_TITLE}
            title={TAB_DETAILS_TITLE}
            headerAs="h2"
            shouldPersistContent
            defaultExpanded={detailsDefaultExpanded}
            onClick={() => {
              trackElementClicked({
                content: 'product_details',
                type: 'tab',
              })
            }}
          >
            {!!alcoholTerms?.data.show_on_pdp && (
              <Box marginBottom="md">
                <AccordionGroup hasLeadingBorder={false}>
                  <Accordion
                    headerAs="h3"
                    title={alcoholTerms.data.heading}
                    leading={<WarningTriangleIcon />}
                  >
                    <div>{alcoholTerms.data.subheading.elements.map(renderElement)}</div>
                  </Accordion>
                  <BottomLine />
                </AccordionGroup>
              </Box>
            )}
            {detailsDefaultExpanded ? (
              <HeightLineClamp height={150} ariaLabel={`${name} $${price}`}>
                {detailsBody}
              </HeightLineClamp>
            ) : (
              detailsBody
            )}
          </AccordionItem>
        )}
        {!!hasProductNutrition && (
          <AccordionItem
            id={NUTRITION_OR_INGREDIENTS_TITLE}
            title={NUTRITION_OR_INGREDIENTS_TITLE}
            headerAs="h2"
            shouldPersistContent
            onClick={() => {
              trackElementClicked({
                content: 'product_nutrition',
                type: 'tab',
              })
            }}
          >
            {!!ingredients && (
              <>
                <Title variant="secondary" strong>
                  Ingredients
                </Title>
                <Body dangerouslySetInnerHTML={{ __html: sanitizeHTML(ingredients) }} />
              </>
            )}
            {/* we ran into hydration issues with the nutrition label in the past and this really only
                needs to be rendered on the client side, so we are wrapping it in ClientOnly */}
            <ClientOnly>
              {nutritionDetailsProducts.length > 0 && (
                <NutritionFacts products={nutritionDetailsProducts} />
              )}
            </ClientOnly>
          </AccordionItem>
        )}
        {specifications.length > 0 && (
          <AccordionItem
            id={TAB_SPECIFICATIONS_TITLE}
            title={TAB_SPECIFICATIONS_TITLE}
            shouldPersistContent
            headerAs="h2"
            onClick={() => {
              trackElementClicked({
                content: 'product_specifications',
                type: 'tab',
              })
            }}
          >
            <Column spacing="md">
              {specifications.map((spec) => {
                const splitted = spec.split(':')

                return (
                  splitted.length === 2 && (
                    <Column key={spec} spacing="md">
                      <Body size="md">
                        <Body as="span" strong size="md">
                          {splitted[0] + ':'}
                        </Body>
                        {splitted[1]}
                      </Body>
                      <BottomLine />
                    </Column>
                  )
                )
              })}
            </Column>
          </AccordionItem>
        )}
      </AccordionGroup>
    </TabPanelContainer>
  )
}

const TabPanelContainer = styled.div`
  margin-top: 0.5rem;
  padding-top: 0.25rem;
`

const Title = styled(Body)`
  margin-bottom: 0.5rem;
`
const BottomLine = styled(Box)`
  border-bottom: ${({ theme }) => theme.color.border.base.neutral.subtle} solid 1px;
`
